import DateFnsUtils from "@date-io/date-fns";
import { t, Trans } from "@lingui/macro";
import { useLingui } from "@lingui/react";
import * as Sentry from "@sentry/browser";
import {
  ApiEpBillingOld,
  ApiEpMarketing,
  apiTokenJwtUpdate,
  FailureApi,
  setDatafanToken,
  setOrganizationId as setOrganizationIdInApi,
} from "@smp/api";
import { UiModuleLoader, UiPickersProvider } from "@smp/ui";
import {
  dataArraySortByProp,
  dataObjectsOneStageObject,
  Failure,
  numberBitGetByPosition,
  REACT_APP__INTERCOM_APP_ID,
  REACT_APP__IS_FOREIGN,
  storeEp,
} from "@smp/utils";
import { format } from "date-fns";
import formatDistance from "date-fns/formatDistance";
import parseISO from "date-fns/parseISO";
import {
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useLocation } from "react-router-dom";

import { PageFailure } from "@/components/feedback/pages/page_failure";
import { useContextEp } from "@/context/ep";
import { useContextLanguage } from "@/context/language";
import { useContextLoader } from "@/context/loader";
import { useContextSmp } from "@/context/smp";
import { useContextSupportMessenger } from "@/context/support_messenger";
import { useContextUi } from "@/context/ui";
import { useEpBillingCurrentPlansubscription } from "@/hooks/use_ep_billing_current_plansubscription";
import { useEpBillingPlansubscriptionList } from "@/hooks/use_ep_billing_plansubscription";
import { useEpBillingRecurringPaymentList } from "@/hooks/use_ep_billing_recurring_payment";
import { useEpOrganizationOrganizationList } from "@/hooks/use_ep_organization";
import { useEpUsersMe } from "@/hooks/use_ep_users_me";
import { useFailureTranslator } from "@/hooks/use_failure_translator";
import { useQueryInvalidation } from "@/screens/app/context/app/hooks/use_query_invalidation";
import {
  getDateFnsLocaleByString,
  getValidLocale,
  gscSendUserParams,
  redirector,
} from "@/utils";

import { IAppPageConfigExtraData } from ".";
import { ContextApp } from "./app.context";
import { IContextApp } from "./app.types";

export const ContextProviderApp = ({ children }: PropsWithChildren<{}>) => {
  const refHeader = useRef<HTMLDivElement | null>(null);
  const { failureTranslationGet } = useFailureTranslator();
  const { changeLanguage } = useContextLanguage();
  const { i18n } = useLingui();
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const [isInited, setIsInited] = useState<boolean>(false);
  const [initialFailure, setInitialFailure] = useState<
    Failure | FailureApi | null
  >(null);
  const [billingStatus, setBillingStatus] =
    useState<IContextApp["state"]["billingStatus"]>(undefined);
  const [isOrganizationContextChanging, setOrganizationContextChanging] =
    useState<IContextApp["state"]["isOrganizationContextChanging"]>(false);
  const [marketingStatus, setMarketingStatus] =
    useState<IContextApp["state"]["marketingStatus"]>(undefined);
  const [organizationId, setOrganizationId] = useState<
    IContextApp["state"]["organizationId"]
  >(storeEp.get("organization_id"));
  const organizationIdChangePendingRef = useRef<
    typeof organizationId | undefined
  >(undefined);

  const { me } = useEpUsersMe();

  const {
    actions: { carrotAuth, carrotIdentify, supportMessangerUpdate },
    state: { service: support_service },
  } = useContextSupportMessenger();

  const {
    actions: {
      epPageConfigsGet,
      epPageConfigAccountsGet,
      epPageConfigAccountsByIdGet,
    },
  } = useContextEp();

  const { organizationList } = useEpOrganizationOrganizationList();

  const {
    state: { config },
  } = useContextLoader();

  const {
    actions: { smpAccountCredentialsGet, smpPagesGet },
    state: { smpPageProxiesMap, smpPagesMap },
  } = useContextSmp();

  const {
    actions: { themeUpdateOnMeChanged },
  } = useContextUi();

  /**
   * get last plansubscription list and
   * calculate very last and active plansubscription
   */

  const {
    currentPlansubscription: plansubscriptionLast,
    isCurrentPlansubscriptionLoading: isPlansubscriptionLastLoading,
  } = useEpBillingCurrentPlansubscription();

  const { isPlansubscriptionListLoading, plansubscriptionList } =
    useEpBillingPlansubscriptionList({
      req: { query: { ordering: "-period_end" } },
    });

  const plansubscriptionActive = plansubscriptionLast
    ? plansubscriptionLast.period_end === null
      ? plansubscriptionLast
      : parseISO(plansubscriptionLast.period_end).getTime() >=
        new Date().getTime()
      ? plansubscriptionLast
      : undefined
    : undefined;

  /**
   * get last recurringPayment list and
   * calculate very last and active recurringPayment
   */

  const { isRecurringPaymentListLoading, recurringPaymentList } =
    useEpBillingRecurringPaymentList({
      req: {
        query: {
          ordering: "-created_at",
          status__in: new Array(Math.pow(2, 4) - 1)
            .fill(undefined)
            .map((_, idx) => idx + 1),
        },
      },
    });

  const recurringPaymentLast = recurringPaymentList
    ? recurringPaymentList[0]
    : undefined;

  const recurringPaymentActive = recurringPaymentList?.find(
    (recurringPayment) =>
      numberBitGetByPosition(recurringPayment.status, 1) === 1 &&
      numberBitGetByPosition(recurringPayment.status, 2) === 0,
  );

  /**
   * calculate active and last plan
   */
  const planActive = plansubscriptionActive
    ? config.billing.plan.find(
        (plan) => plan.id === plansubscriptionActive.plan_id,
      )
    : undefined;

  const planLast = plansubscriptionLast
    ? config.billing.plan.find(
        (plan) => plan.id === plansubscriptionLast.plan_id,
      )
    : undefined;

  const organizationIdStored = useRef<typeof organizationId | undefined>(
    undefined,
  );

  const accessRightsCheck: IContextApp["actions"]["accessRightsCheck"] =
    useCallback(
      (action) => {
        if (organizationId === null) return "allowed";
        const organization =
          organizationList &&
          organizationList.find(
            (organization) => organization.id === organizationId,
          );
        return organization
          ? organization.role === "observer_v1"
            ? "denied"
            : organization.role === "editor_v1"
            ? action === "story_create"
              ? "allowed"
              : "denied"
            : organization.role === "manager_v1"
            ? action === "url_shortener_outer"
              ? "denied"
              : action === "socialaccount_connect"
              ? "denied"
              : "allowed"
            : "allowed"
          : "denied";
      },
      [organizationList, organizationId],
    );

  const organizationIdChange = useCallback<
    IContextApp["actions"]["organizationIdChange"]
  >(({ organizationId }) => {
    setOrganizationIdInApi(organizationId);
    storeEp.set("organization_id", organizationId);
    organizationIdChangePendingRef.current = organizationId;

    apiTokenJwtUpdate().then(() => {
      if (organizationIdChangePendingRef.current !== organizationId) {
        return;
      }

      setDatafanToken(null);
      setOrganizationId(organizationId);
    });

    return organizationId;
  }, []);

  const billingStatusGet: IContextApp["actions"]["billingStatusGet"] =
    useCallback(async () => {
      try {
        const billingStatus = await ApiEpBillingOld.Status.retrieve();
        if (billingStatus.scheduled) {
          const mediumExternalIds =
            billingStatus.scheduled.length &&
            billingStatus.scheduled.map(
              (item) =>
                `${item.page_config.medium}:${item.page_config.external_id}`,
            );
          mediumExternalIds &&
            (await smpPagesGet({ medium_external_id__in: mediumExternalIds }));
        }
        if (billingStatus.base_policy === "classic") {
          const mediumExternalIds =
            billingStatus.unlimited_pages.active.length &&
            billingStatus.unlimited_pages.active.map(
              (pageconfig) => `${pageconfig.medium}:${pageconfig.external_id}`,
            );
          mediumExternalIds &&
            (await smpPagesGet({ medium_external_id__in: mediumExternalIds }));
        }
        setBillingStatus(billingStatus);
      } catch (error) {
        const failureApi = error as FailureApi;
        if (
          failureApi.code === "API__ROOT__ORGANIZATION_NOT_FOUND" ||
          failureApi.code === "API__ROOT__ORGANIZATION_TEMPORARILY_INACTIVE"
        ) {
          organizationIdChange({ organizationId: null });
        }
      }
    }, [organizationIdChange, smpPagesGet]);

  const drawerExpandToggle: IContextApp["actions"]["drawerExpandToggle"] =
    useCallback((value) => {
      setIsExpanded((state) => (value === undefined ? !state : value));
    }, []);

  const authNextUrl = useMemo(() => redirector.getNext(), []);

  const isMeOrganizationOwner = useMemo(() => {
    const organization =
      organizationList &&
      organizationId &&
      organizationList.find(
        (organization) => organization.id === organizationId,
      );
    return (organization && organization.owner?.id) === (me && me.id);
  }, [organizationList, me, organizationId]);

  const isPersonalContext = useMemo(
    () => !organizationId || !me,
    [me, organizationId],
  );

  const isPersonalOrOwnOrganizationContext = useMemo(
    () => isPersonalContext || isMeOrganizationOwner,
    [isPersonalContext, isMeOrganizationOwner],
  );

  const crossDomainRedirectLocation = useMemo(
    () => me && redirector.getCrossDomainRedirectLocation(me),
    [me],
  );

  const billingPaymentType = useMemo(() => {
    const sp = new URLSearchParams(location.search);

    if (
      (me && ["cis", "europe", "world"].includes(me.billing_policy_id)) ||
      recurringPaymentActive ||
      sp.get("payment_type")
    ) {
      return "recurring";
    } else {
      return "single";
    }
  }, [location.search, me, recurringPaymentActive]);

  const pageConfigExtraDataGet: IContextApp["actions"]["pageConfigExtraDataGet"] =
    useCallback(
      (pageConfigId, pageConfigExternalId, medium) => {
        const hash = smpPageProxiesMap.get(`${medium}:${pageConfigExternalId}`);
        const smpExtraData = hash ? smpPagesMap.get(hash) : undefined;
        const pageconfigaccounts = epPageConfigAccountsByIdGet(pageConfigId);

        let result: IAppPageConfigExtraData = {};

        if (pageconfigaccounts) {
          const pageConfigAccountSortedByPriority = pageconfigaccounts
            .sort(dataArraySortByProp((item) => item.id))
            .sort(dataArraySortByProp((item) => item.priority));
          const pageConfigHighPriority =
            pageConfigAccountSortedByPriority[
              pageConfigAccountSortedByPriority.length - 1
            ];
          result.account = pageConfigHighPriority.account;
          result.has_publish_permission =
            pageConfigAccountSortedByPriority.some(
              ({ has_publish_permission }) => has_publish_permission,
            );
        }

        if (smpExtraData) {
          const { name, extra_data } = smpExtraData;
          result = { ...result, name, ...extra_data };
        }

        return !pageconfigaccounts
          ? { ...result, has_publish_permission: false }
          : result;
      },
      [epPageConfigAccountsByIdGet, smpPageProxiesMap, smpPagesMap],
    );

  const pageConfigWithExtraDataGet: IContextApp["actions"]["pageConfigWithExtraDataGet"] =
    useCallback(
      (pageConfig) => {
        const extraData = pageConfigExtraDataGet(
          pageConfig.id,
          pageConfig.external_id,
          pageConfig.medium,
        );
        return {
          ...extraData,
          ...pageConfig,
          has_publish_permission:
            pageConfig.has_publish_permission ??
            extraData.has_publish_permission ??
            true,
          logo: extraData.logo,
          name:
            pageConfig.name && !!pageConfig.name.trim()
              ? pageConfig.name
              : extraData.name && !!extraData.name.trim()
              ? extraData.name
              : i18n._(t`Unknown`),
        };
      },
      [i18n, pageConfigExtraDataGet],
    );

  const pageConfigWithoutExtraDataGet: IContextApp["actions"]["pageConfigWithoutExtraDataGet"] =
    useCallback((pageConfigWithExtraData) => {
      const {
        account,
        is_business,
        is_private,
        logo,
        screen_name,
        type,
        ...cleanPageConfig
      } = pageConfigWithExtraData;
      return cleanPageConfig;
    }, []);

  const marketingStatusGet: IContextApp["actions"]["marketingStatusGet"] =
    useCallback(async () => {
      const marketingStatus = await ApiEpMarketing.Status.retrieve();
      setMarketingStatus(marketingStatus);
      return marketingStatus;
    }, []);

  useEffect(() => {
    if (organizationIdStored.current !== organizationId) {
      organizationIdStored.current = organizationId;
      setOrganizationContextChanging(true);
    }
  }, [organizationId]);

  /**
   * Update language code on `me` changed
   */
  useEffect(() => {
    const lang = me?.language || "en";

    !!me && changeLanguage(getValidLocale(lang));
    document.documentElement.setAttribute("lang", lang);
  }, [me, i18n, changeLanguage]);

  /**
   * Update theme on `me` changed
   */
  useEffect(() => {
    !!me && themeUpdateOnMeChanged(me);
  }, [me, themeUpdateOnMeChanged]);

  /**
   * Redirect user if backend's hostname is not equal to browser's
   */
  useEffect(() => {
    if (crossDomainRedirectLocation) {
      window.location.href = crossDomainRedirectLocation;
    }
  }, [crossDomainRedirectLocation]);

  /**
   * Auth CarrotQuest on
   * `meId | meCarrotQuestHash | configBackendSmpAppId`
   * changed
   */
  useEffect(() => {
    if (
      !carrotAuth ||
      !config.backend.smp_app_id ||
      !me?.carrotquest_hash ||
      !me?.id
    )
      return;

    carrotAuth(`${config.backend.smp_app_id}:${me.id}`, me.carrotquest_hash);
  }, [carrotAuth, config.backend.smp_app_id, me?.carrotquest_hash, me?.id]);

  /**
   * Identify CarrotQuest on `me` changed
   */
  useEffect(() => {
    if (!carrotIdentify || !me) return;

    carrotIdentify({
      $email: me.email,
      $name: `${me.first_name} ${me.last_name}`,
      created_at: me.date_joined,
      email_verified: me.is_email_verified,
      language_override: me.language,
      user_hash: me.carrotquest_hash,
      user_id: me.email,
    });
  }, [carrotIdentify, me]);

  useEffect(() => {
    if (isPlansubscriptionLastLoading || !marketingStatus || !me) return;

    gscSendUserParams({
      billing_policy_id: me.billing_policy_id,
      is_email_verified: me.is_email_verified,
      is_foreign: REACT_APP__IS_FOREIGN,
      is_payment_canceled: false,
      is_popup_enabled: me.settings.ui.is_popup_enabled,
      is_trial: planActive?.is_trial || false,
      language: me.language,
      user_id: me.id,
      email: me.email,
      date_joined: format(new Date(me.date_joined), "dd.MM.yyyy HH:mm"),
      ...dataObjectsOneStageObject(marketingStatus),
    });
  }, [isPlansubscriptionLastLoading, marketingStatus, me, planActive]);

  /**
   * Identify CarrotQuest on marketingStatus changed
   */
  useEffect(() => {
    if (!carrotIdentify || !marketingStatus) return;

    carrotIdentify(marketingStatus);
  }, [carrotIdentify, marketingStatus]);

  /**
   * Identify CarrotQuest on billingStatus changed
   */
  useEffect(() => {
    if (!carrotIdentify || !billingStatus) return;

    carrotIdentify({
      active_products_info:
        billingStatus.base_policy === "classic"
          ? `${
              billingStatus.posts_balance.free +
              billingStatus.posts_balance.paid
            } posts, ${
              billingStatus.unlimited_pages.active.length
            } active unlimited pages`
          : billingStatus.base_policy === "world"
          ? billingStatus.subscription
            ? (() => {
                const isExpired =
                  parseISO(billingStatus.subscription.period_end).getTime() <=
                  new Date().getTime();
                const expiredAgo =
                  isExpired &&
                  formatDistance(
                    parseISO(billingStatus.subscription.period_end),
                    Date.now(),
                  );
                const activeLeft =
                  !isExpired &&
                  formatDistance(
                    parseISO(billingStatus.subscription.period_end),
                    Date.now(),
                  );
                return `${billingStatus.subscription.plan.id}, ${
                  isExpired ? `expired ${expiredAgo} ago` : `${activeLeft} left`
                }`;
              })()
            : false
          : false,
      base_policy: billingStatus.base_policy,
      is_payer:
        billingStatus.base_policy === "world" && billingStatus.subscription
          ? billingStatus.subscription?.plan.id !== "free" &&
            billingStatus.subscription?.plan.id !== "free_foreign" &&
            parseISO(billingStatus.subscription?.period_end).getTime() >=
              new Date().getTime()
          : false,
      policy_id: billingStatus.policy_id,
    });
  }, [billingStatus, carrotIdentify, me, me?.jivo_token]);

  /**
   * Update Intercom user info on `me` or `billingStatus` changed
   */
  useEffect(() => {
    if (me && me.is_authorized) {
      supportMessangerUpdate({
        policy_id: billingStatus?.policy_id,
        active_products_info:
          billingStatus?.base_policy === "classic"
            ? `${
                billingStatus.posts_balance.free +
                billingStatus.posts_balance.paid
              } posts, ${
                billingStatus.unlimited_pages.active.length
              } active unlimited pages`
            : billingStatus?.base_policy === "world"
            ? billingStatus.subscription
              ? (() => {
                  const isExpired =
                    parseISO(billingStatus.subscription.period_end).getTime() <=
                    new Date().getTime();
                  const expiredAgo =
                    isExpired &&
                    formatDistance(
                      parseISO(billingStatus.subscription.period_end),
                      Date.now(),
                    );
                  const activeLeft =
                    !isExpired &&
                    formatDistance(
                      parseISO(billingStatus.subscription.period_end),
                      Date.now(),
                    );
                  return `${billingStatus.subscription.plan.id}, ${
                    isExpired
                      ? `expired ${expiredAgo} ago`
                      : `${activeLeft} left`
                  }`;
                })()
              : false
            : false,
        app_id: REACT_APP__INTERCOM_APP_ID,
        created_at: me.date_joined,
        email: me.email,
        email_verified: me.is_email_verified,
        is_payer:
          billingStatus?.base_policy === "world" && billingStatus.subscription
            ? billingStatus.subscription?.plan.id !== "free" &&
              billingStatus.subscription?.plan.id !== "free_foreign" &&
              parseISO(billingStatus.subscription?.period_end).getTime() >=
                new Date().getTime()
            : false,
        language_override: me.language,
        name: me.first_name,
        user_hash: me.intercom_hash,
        user_id: support_service === "intercom" ? me.email : me.id,
        jivo_token: me?.jivo_token,
      });
    } else {
      supportMessangerUpdate({
        app_id: REACT_APP__INTERCOM_APP_ID,
      });
    }
  }, [billingStatus, supportMessangerUpdate, me, support_service]);

  /**
   * Update Sentry user on `me` changed
   */
  useEffect(() => {
    !!me &&
      Sentry.setUser({
        email: me.email,
        id: "" + me.id,
        username: `${me.first_name} ${me.last_name}`,
      });
  }, [me]);

  /**
   * Update Sentry extra on `billingStatus` changed
   */
  useEffect(() => {
    !!billingStatus &&
      Sentry.setExtra("billing_status", {
        base_policy: billingStatus.base_policy,
        policy_id: billingStatus.policy_id,
      });
  }, [billingStatus]);

  useEffect(() => {
    if (
      authNextUrl &&
      redirector.getAppPathname(authNextUrl) === location.pathname &&
      me?.is_email_verified
    ) {
      redirector.clearNext();
    }
  }, [authNextUrl, location.pathname, me?.is_email_verified]);

  /**
   * Invalidate requests on change organizationId
   */
  useQueryInvalidation(organizationId);

  /**
   * App initialization
   */
  useEffect(() => {
    (async () => {
      if (!me) {
        return;
      }

      try {
        await billingStatusGet();

        await marketingStatusGet();
        const epPageConfigs = await epPageConfigsGet({
          fields: ["post_time_set"],
        });
        const smpAccountCredentials = await smpAccountCredentialsGet({
          query: {
            is_lost: false,
            type__in: [
              "instagram_legacy1",
              "token",
              "oauth1_token",
              "refreshable_token",
              "group_token_nested",
              "app_credential_proxy",
              "instagram_business",
              "another_credential_proxy",
              "cookies",
            ],
          },
        });
        await smpPagesGet({
          id__in: smpAccountCredentials.map(
            (smpAccountCredential) => smpAccountCredential.account_page_id,
          ),
        });
        epPageConfigs.length > 0 &&
          (await smpPagesGet({
            medium_external_id__in: epPageConfigs.map(
              (epPageConfig) =>
                `${epPageConfig.medium}:${epPageConfig.external_id}`,
            ),
          }));
        await epPageConfigAccountsGet();
        setIsInited(true);
        setOrganizationContextChanging(false);
      } catch (clause) {
        const failureOrFailureApi = clause as Failure | FailureApi;
        if (
          failureOrFailureApi.code === "API__ROOT__ORGANIZATION_NOT_FOUND" ||
          failureOrFailureApi.code ===
            "API__ROOT__ORGANIZATION_TEMPORARILY_INACTIVE" ||
          failureOrFailureApi.code ===
            "API__EP__ORGANIZATION__ORGANIZATION_NOT_FOUND" ||
          failureOrFailureApi.code ===
            "API__EP__ORGANIZATION__ORGANIZATION_TEMPORARILY_INACTIVE" ||
          failureOrFailureApi.code === "API__ROOT__FETCH__NO__RESPONSE" || // TODO: we should rebuild the condition and the redirect logic in esle (https://yt.bind.pw/issue/SF-158)
          failureOrFailureApi.code ===
            "API__EP__ORGANIZATION__ORGANIZATION_LIMIT_HIT"
        ) {
          organizationIdChange({ organizationId: null });
        } else {
          if (failureOrFailureApi.code !== "API__INTERNAL__403_FORBIDDEN") {
            setInitialFailure(failureOrFailureApi);
          }
          return;
        }
      }
    })();
  }, [
    billingStatusGet,
    epPageConfigAccountsGet,
    epPageConfigsGet,
    marketingStatusGet,
    me,
    organizationId,
    organizationIdChange,
    smpAccountCredentialsGet,
    smpPagesGet,
  ]);

  return useMemo(() => {
    if (initialFailure) {
      return (
        <PageFailure
          errorMessage={
            initialFailure instanceof FailureApi
              ? failureTranslationGet(initialFailure)
              : initialFailure.code
          }
          errorCode={initialFailure.code}
          title={<Trans>Application loading error</Trans>}
        />
      );
    }

    if (!isInited || !me || crossDomainRedirectLocation) {
      return <UiModuleLoader loadingText={<Trans>Loading</Trans>} />;
    }

    if (
      (!me.is_authorized || !me.is_email_verified) &&
      location.pathname !== "/email_verification"
    ) {
      return <Navigate to={redirector.getEmailVerificationLocation()} />;
    }

    return (
      <UiPickersProvider
        dateAdapter={DateFnsUtils as any}
        locale={getDateFnsLocaleByString(i18n.locale)}
      >
        <ContextApp.Provider
          value={{
            actions: {
              accessRightsCheck,
              billingStatusGet,
              drawerExpandToggle,
              marketingStatusGet,
              organizationIdChange,
              pageConfigExtraDataGet,
              pageConfigWithExtraDataGet,
              pageConfigWithoutExtraDataGet,
            },
            refs: {
              header: refHeader,
            },
            state: {
              billingStatus,
              billingPaymentType,
              isDangerous: !REACT_APP__IS_FOREIGN,
              isExpanded,
              isMeOrganizationOwner,
              isOrganizationContextChanging,
              isPersonalContext,
              isPersonalOrOwnOrganizationContext,
              isPlansubscriptionListLoading,
              isRecurringPaymentListLoading,
              marketingStatus,
              me,
              organizationId,
              planActive,
              planLast,
              plansubscriptionActive,
              plansubscriptionLast,
              plansubscriptionList,
              recurringPaymentActive,
              recurringPaymentLast,
              recurringPaymentList,
            },
          }}
        >
          {children}
        </ContextApp.Provider>
      </UiPickersProvider>
    );
  }, [
    accessRightsCheck,
    billingPaymentType,
    billingStatus,
    billingStatusGet,
    children,
    crossDomainRedirectLocation,
    drawerExpandToggle,
    failureTranslationGet,
    i18n.locale,
    initialFailure,
    isExpanded,
    isInited,
    isMeOrganizationOwner,
    isOrganizationContextChanging,
    isPersonalContext,
    isPersonalOrOwnOrganizationContext,
    isPlansubscriptionListLoading,
    isRecurringPaymentListLoading,
    location.pathname,
    marketingStatus,
    marketingStatusGet,
    me,
    organizationId,
    organizationIdChange,
    pageConfigExtraDataGet,
    pageConfigWithExtraDataGet,
    pageConfigWithoutExtraDataGet,
    planActive,
    planLast,
    plansubscriptionActive,
    plansubscriptionLast,
    plansubscriptionList,
    recurringPaymentActive,
    recurringPaymentLast,
    recurringPaymentList,
  ]);
};
